/**
 * @since 2026-04-22
 * @author Francesco Parrella
 * @maintainer Francesco Parrella
 * @copyright AlgoTraders, All rights reserved
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { notifyBrowser, useNotification } from "components/Shared/Notifications";
import { listFixJobs } from "services/api";

const POLL_INTERVAL_MS = 3000;
const TERMINAL_STATES = new Set(["succeeded", "failed", "stale"]);
const FETCH_LIMIT = 10;

const keyFor = (securityName, barSize) => `${securityName}|${barSize}`;

export const useActiveFixJobs = ({ idToken, getToken, enabled = true } = {}) => {
  const [jobs, setJobs] = useState([]);
  const [error, setError] = useState(null);
  const [completionTick, setCompletionTick] = useState(0);
  const timerRef = useRef(null);
  const cancelledRef = useRef(false);
  const seenJobsRef = useRef(new Map());
  const seededRef = useRef(false);
  const { notifySuccess, notifyError } = useNotification();

  const fetchOnce = useCallback(async () => {
    if (!idToken || !enabled) return;
    try {
      const response = await listFixJobs(FETCH_LIMIT, idToken, getToken);
      if (cancelledRef.current) return;
      const nextJobs = response?.jobs || [];

      const seen = seenJobsRef.current;
      let justCompleted = false;
      for (const job of nextJobs) {
        const prevState = seen.get(job.jobId);
        if (seededRef.current && prevState !== undefined
            && !TERMINAL_STATES.has(prevState) && TERMINAL_STATES.has(job.state)) {
          const tag = `fixJob:${job.jobId}`;
          if (job.state === "succeeded") {
            notifySuccess(`Fix job complete — ${job.done || 0} of ${job.total || 0} updated`);
            notifyBrowser("Fix job complete", `${job.done || 0} of ${job.total || 0} updated`, { tag });
          } else if (job.state === "failed") {
            notifyError(`Fix job failed (${job.errored || 0} errored)`);
            notifyBrowser("Fix job failed", `${job.errored || 0} errored`, { tag });
          } else {
            notifyError("Fix job went stale — check backend logs");
            notifyBrowser("Fix job stale", "Check backend logs", { tag });
          }
          justCompleted = true;
        }
        seen.set(job.jobId, job.state);
      }
      seededRef.current = true;
      if (justCompleted) setCompletionTick((t) => t + 1);

      setJobs(nextJobs);
      setError(null);
    } catch (e) {
      if (cancelledRef.current) return;
      setError(e);
    }
  }, [idToken, getToken, enabled, notifySuccess, notifyError]);

  useEffect(() => {
    cancelledRef.current = false;
    if (!enabled || !idToken) return () => {};

    const scheduleNext = () => {
      if (cancelledRef.current) return;
      if (typeof document !== "undefined" && document.hidden) return;
      timerRef.current = setTimeout(tick, POLL_INTERVAL_MS);
    };

    const tick = async () => {
      await fetchOnce();
      scheduleNext();
    };

    const onVisibility = () => {
      if (typeof document !== "undefined" && !document.hidden) {
        if (timerRef.current) clearTimeout(timerRef.current);
        tick();
      }
    };

    tick();
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", onVisibility);
    }

    return () => {
      cancelledRef.current = true;
      if (timerRef.current) clearTimeout(timerRef.current);
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", onVisibility);
      }
    };
  }, [fetchOnce, idToken, enabled]);

  const { activeJobs, runningKeys, currentKey } = useMemo(() => {
    const active = jobs.filter((j) => !TERMINAL_STATES.has(j.state));
    const running = new Set();
    let current = null;
    for (const job of active) {
      const done = new Set(
        (job.items || []).map((it) => keyFor(it.securityName, it.barSize))
      );
      for (const entry of job.scope || []) {
        const k = keyFor(entry.securityName, entry.barSize);
        if (!done.has(k)) running.add(k);
      }
      if (job.currentItem && !current) {
        current = keyFor(job.currentItem.securityName, job.currentItem.barSize);
      }
    }
    return { activeJobs: active, runningKeys: running, currentKey: current };
  }, [jobs]);

  return { activeJobs, runningKeys, currentKey, error, completionTick, refetch: fetchOnce };
};

export default useActiveFixJobs;
